import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const LatestThoughts = () => {
  const blogPosts = [
    {
      id: 1,
      title: "Building Scalable React Applications: Lessons from Production",
      excerpt: "Exploring advanced patterns and architectural decisions that helped scale a React application from 10K to 1M+ users. Key insights on state management, code splitting, and performance optimization.",
      category: "React",
      readTime: "8 min read",
      publishDate: "2025-01-15",
      image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=250&fit=crop",
      tags: ["React", "Performance", "Architecture"],
      featured: true
    },
    {
      id: 2,
      title: "The Future of Web Development: AI-Assisted Coding",
      excerpt: "How AI tools are transforming the development workflow and what it means for developers. A deep dive into GitHub Copilot, ChatGPT, and emerging AI coding assistants.",
      category: "AI & Development",
      readTime: "6 min read",
      publishDate: "2025-01-10",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop",
      tags: ["AI", "Productivity", "Future"],
      featured: true
    },
    {
      id: 3,
      title: "Microservices vs Monolith: When to Choose What",
      excerpt: "A practical guide to architectural decisions based on real-world experience. Understanding trade-offs, team size, and business requirements in choosing the right architecture.",
      category: "Architecture",
      readTime: "10 min read",
      publishDate: "2025-01-05",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=250&fit=crop",
      tags: ["Architecture", "Backend", "Strategy"],
      featured: false
    },
    {
      id: 4,
      title: "CSS Grid vs Flexbox: A Complete Comparison Guide",
      excerpt: "Understanding when to use CSS Grid versus Flexbox with practical examples and real-world use cases. Complete guide with interactive demos and best practices.",
      category: "CSS",
      readTime: "7 min read",
      publishDate: "2024-12-28",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=250&fit=crop",
      tags: ["CSS", "Layout", "Frontend"],
      featured: false
    }
  ];

  const featuredPosts = blogPosts?.filter(post => post?.featured);
  const recentPosts = blogPosts?.filter(post => !post?.featured)?.slice(0, 2);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="PenTool" size={20} className="text-accent" />
            <span className="text-sm font-medium text-text-secondary tracking-wide uppercase">
              Latest Thoughts
            </span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-4">
            Technical Insights & Learning
          </h2>
          
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Sharing knowledge, experiences, and insights from the world of software development. 
            Exploring new technologies, best practices, and lessons learned.
          </p>
        </div>

        {/* Featured Posts */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {featuredPosts?.map((post, index) => (
            <article
              key={post?.id}
              className="group bg-card rounded-xl shadow-soft hover:shadow-large transition-all duration-500 overflow-hidden animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Post Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={post?.image}
                  alt={post?.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full">
                    {post?.category}
                  </span>
                </div>

                {/* Reading Time */}
                <div className="absolute top-4 right-4">
                  <div className="flex items-center space-x-1 bg-black/50 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs">
                    <Icon name="Clock" size={12} />
                    <span>{post?.readTime}</span>
                  </div>
                </div>
              </div>

              {/* Post Content */}
              <div className="p-6">
                {/* Meta Information */}
                <div className="flex items-center space-x-4 mb-3 text-sm text-text-secondary">
                  <div className="flex items-center space-x-1">
                    <Icon name="Calendar" size={14} />
                    <span>{formatDate(post?.publishDate)}</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-primary mb-3 group-hover:text-accent transition-colors duration-300 line-clamp-2">
                  {post?.title}
                </h3>

                {/* Excerpt */}
                <p className="text-text-secondary text-sm mb-4 line-clamp-3">
                  {post?.excerpt}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {post?.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-muted text-text-secondary text-xs rounded-md hover:bg-accent hover:text-accent-foreground transition-colors duration-300 cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Read More */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="text-accent hover:text-accent-foreground hover:bg-accent p-0"
                  >
                    Read Article
                  </Button>
                  
                  <div className="flex items-center space-x-2">
                    <button className="p-2 rounded-lg text-text-secondary hover:text-accent hover:bg-muted transition-all duration-300">
                      <Icon name="Heart" size={16} />
                    </button>
                    <button className="p-2 rounded-lg text-text-secondary hover:text-accent hover:bg-muted transition-all duration-300">
                      <Icon name="Share2" size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Recent Posts List */}
        <div className="bg-card rounded-xl p-8 shadow-soft animate-fade-in-up" style={{ animationDelay: '300ms' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-primary">More Recent Posts</h3>
            <Button
              variant="ghost"
              size="sm"
              iconName="ArrowRight"
              iconPosition="right"
              className="text-accent hover:text-accent-foreground hover:bg-accent"
            >
              View All Posts
            </Button>
          </div>

          <div className="space-y-6">
            {recentPosts?.map((post) => (
              <article
                key={post?.id}
                className="flex items-start space-x-4 p-4 rounded-lg hover:bg-muted transition-colors duration-300 group cursor-pointer"
              >
                {/* Post Thumbnail */}
                <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden">
                  <Image
                    src={post?.image}
                    alt={post?.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* Post Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="px-2 py-1 bg-accent/10 text-accent text-xs font-medium rounded">
                      {post?.category}
                    </span>
                    <span className="text-xs text-text-secondary">
                      {formatDate(post?.publishDate)}
                    </span>
                    <span className="text-xs text-text-secondary">•</span>
                    <span className="text-xs text-text-secondary">{post?.readTime}</span>
                  </div>
                  
                  <h4 className="font-semibold text-primary group-hover:text-accent transition-colors duration-300 mb-2 line-clamp-2">
                    {post?.title}
                  </h4>
                  
                  <p className="text-sm text-text-secondary line-clamp-2">
                    {post?.excerpt}
                  </p>
                </div>

                {/* Arrow Icon */}
                <div className="flex-shrink-0">
                  <Icon 
                    name="ArrowRight" 
                    size={16} 
                    className="text-text-secondary group-hover:text-accent group-hover:translate-x-1 transition-all duration-300" 
                  />
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="mt-12 bg-gradient-to-r from-accent to-primary rounded-xl p-8 text-center animate-fade-in-up" style={{ animationDelay: '400ms' }}>
          <div className="max-w-2xl mx-auto">
            <Icon name="Mail" size={32} className="text-accent-foreground mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-accent-foreground mb-4">
              Stay Updated with Latest Insights
            </h3>
            <p className="text-accent-foreground/80 mb-6">
              Get notified when I publish new articles about web development, 
              best practices, and emerging technologies.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-accent-foreground focus:outline-none"
              />
              <Button
                variant="secondary"
                iconName="Send"
                iconPosition="right"
                className="bg-accent-foreground text-accent hover:bg-accent-foreground/90"
              >
                Subscribe
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LatestThoughts;